import * as z from "zod";
import { anyOfFilters, type Caller, flag, pagination, type Result, version } from "../index";
import { aggregateSyncDto, syncStatuses } from "../til";
import { fulfillmentStatuses } from "./enums";

// Fulfillment of a sales order (M13, FR-M13-05, FR-M13-06, ADR-126; 50-api/logistics.md): the shipment the warehouse
// makes of the units allocated to an order, the anchor of its documents (FR-M9-06) and of the instruction to the
// warehouse (I-09). Created, released and cancelled only by the process of the order (`order-fulfillment`, P5.3f),
// with the commands below.

/** Permissions of the system Actors of the commands, given by the process that calls them: no role may hold them. */
export const FULFILLMENT_CREATE = "fulfillment.create";
export const FULFILLMENT_RELEASE = "fulfillment.release";
export const FULFILLMENT_CANCEL = "fulfillment.cancel";

/**
 * The lot of the order a shipment is for (ADR-178 §1.7, ADR-195): what one lock froze. Without it, the one shipment of
 * an order locked before the lots (ADR-126).
 */
const lot = z.number().int().positive().optional();

/**
 * `createFulfillment` (processi-e-viste.md §3, ADR-097, ADR-126): the shipment of the units a lot of a locked order
 * holds, one per lot (ADR-195). Its key is the caller's, the sales order and its lot: the same command again answers
 * that shipment as it is now.
 */
export const createFulfillmentInput = z.object({ soId: z.uuid(), lot });
export type CreateFulfillmentInput = z.input<typeof createFulfillmentInput>;

/** `releaseFulfillment`: the shipment goes to the warehouse with its document (I-09); again, it answers as it is. */
export const releaseFulfillmentInput = z.object({ fulfillmentId: z.uuid() });
export type ReleaseFulfillmentInput = z.input<typeof releaseFulfillmentInput>;

/**
 * `cancelFulfillment`, the compensation of both commands (ADR-097 point 7): by the key of `createFulfillment`, because
 * who calls it does not know whether a command committed. Nothing to cancel is a success.
 */
export const cancelFulfillmentInput = z.object({ soId: z.uuid(), lot });
export type CancelFulfillmentInput = z.input<typeof cancelFulfillmentInput>;

const fulfillmentLineDto = z.object({
  id: z.uuid(),
  soLineId: z.uuid(),
  allocationId: z.uuid().nullable(),
  skuId: z.uuid(),
  qty: z.number().int(),
  /** Units the warehouse shipped so far (I-11, ADR-130): more than `qty` when it shipped more. */
  qtyShipped: z.number().int(),
});

/** A shipment as its commands answer it. */
export const fulfillmentDto = z
  .object({
    id: z.uuid(),
    fulfillmentNumber: z.string(),
    soId: z.uuid(),
    /** The lot of the order it ships (ADR-195); null for an order locked before the lots. */
    lotNo: z.number().int().nullable(),
    customerOrgId: z.uuid(),
    locationId: z.uuid(),
    legalEntityId: z.uuid(),
    /**
     * ADR-197: its company invoices after the shipment (frozen when the shipment of a lot was born): it is released
     * without a document, and invoiced on the units shipped.
     */
    invoiceAfterShipment: z.boolean(),
    status: z.enum(fulfillmentStatuses),
    /** When the instruction left for the warehouse (I-09). */
    shippingInstructionSentAt: z.iso.datetime().nullable(),
    /** When the warehouse took it (FR-M13-06). */
    acknowledgedAt: z.iso.datetime().nullable(),
    /** When its cancellation was sent to the warehouse, which has still to answer. */
    cancelRequestedAt: z.iso.datetime().nullable(),
    /** When the warehouse said it ships nothing more (`shipped`, I-11). */
    shippedAt: z.iso.datetime().nullable(),
    syncStatus: z.enum(syncStatuses),
    version: z.number().int(),
    lines: z.array(fulfillmentLineDto),
  })
  .meta({ id: "Fulfillment" });
export type FulfillmentDto = z.output<typeof fulfillmentDto>;

/**
 * What `cancelFulfillment` answers (ADR-097 point 7): whether the shipment is cancelled now, or its cancellation is on
 * its way to the warehouse; neither, the warehouse can no longer stop it. `null` when the order never had one.
 */
export const fulfillmentCancellationDto = z
  .object({
    cancelled: z.boolean(),
    pending: z.boolean(),
    fulfillment: fulfillmentDto.nullable(),
  })
  .meta({ id: "FulfillmentCancellation" });
export type FulfillmentCancellationDto = z.output<typeof fulfillmentCancellationDto>;

/**
 * The commands of `LogisticsService` the processes of sales call (ADR-097): the entrypoint implements them, so the
 * caller and the binding cannot drift apart.
 */
export interface FulfillmentCommands {
  createFulfillment(caller: Caller, input: CreateFulfillmentInput): Promise<Result<FulfillmentDto>>;
  releaseFulfillment(caller: Caller, input: ReleaseFulfillmentInput): Promise<Result<FulfillmentDto>>;
  cancelFulfillment(caller: Caller, input: CancelFulfillmentInput): Promise<Result<FulfillmentCancellationDto>>;
}

// --- The state of a shipment towards the warehouse (I-09 §6, §7) ---

export const getFulfillmentSyncInput = z.object({ fulfillmentId: z.uuid() });
export type GetFulfillmentSyncInput = z.input<typeof getFulfillmentSyncInput>;
/** A shipment by its id: the path of the routes of one shipment. */
export const fulfillmentParams = getFulfillmentSyncInput.pick({ fulfillmentId: true });

/**
 * The Logistics sends a released shipment to the warehouse again, as a new revision: after a refusal, a message Nexxt
 * gave up on, or an outcome that never arrived. `version` is the one the client read (409 otherwise).
 */
export const resyncFulfillmentInput = getFulfillmentSyncInput.extend({ version });
export type ResyncFulfillmentInput = z.input<typeof resyncFulfillmentInput>;
export const resyncFulfillmentBody = resyncFulfillmentInput.omit({ fulfillmentId: true });

export const fulfillmentSyncDto = aggregateSyncDto
  .omit({ externalId: true })
  .extend({ fulfillmentId: z.uuid() })
  .meta({ id: "FulfillmentSync" });

// --- The shipments as Backstage reads them (FR-M13-02, FR-M13-06, ADR-152) ---

const fulfillmentFilters = {
  status: z.enum(fulfillmentStatuses).optional(),
  /** The warehouse it leaves from. */
  location: z.uuid().optional(),
  /** The shipments of a sales order: one per lot (ADR-195), one at most for an order locked before the lots. */
  soId: z.uuid().optional(),
  syncStatus: z.enum(syncStatuses).optional(),
  /**
   * FR-M13-02: Tomorrow ships it (not EXW), the warehouse has it, and no shipment says its tracking yet: the Customer
   * Service books it by hand (`toBook`).
   */
  toBook: flag.optional(),
  /** FR-M13-06: released, and the warehouse has not taken it past `fulfillment.ack_overdue_minutes` (`ackOverdue`). */
  ackOverdue: flag.optional(),
};

/** The shipments in at least one of these sets of the filters (ADR-302): the queues of the shipments, to do. */
const anyOfFulfillments = { anyOf: anyOfFilters(fulfillmentFilters).optional() };

/** The shipments, the latest born first. */
export const listFulfillmentsInput = z.object({
  ...fulfillmentFilters,
  ...anyOfFulfillments,
  /** Part of the number of the shipment or of its sales order, case-insensitive and without separators (ADR-117). */
  q: z.string().trim().min(1).max(80).optional(),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListFulfillmentsInput = z.input<typeof listFulfillmentsInput>;

/**
 * How many shipments with the same filters, and since when the oldest (ADR-086): since its release for the two
 * queues of the home, since its birth otherwise.
 */
export const countFulfillmentsInput = z.object({ ...fulfillmentFilters, ...anyOfFulfillments });
export type CountFulfillmentsInput = z.input<typeof countFulfillmentsInput>;

/** A shipment with its lines. */
export const getFulfillmentInput = z.object({ fulfillmentId: z.uuid() });
export type GetFulfillmentInput = z.input<typeof getFulfillmentInput>;

/** A shipment as its list shows it, with what the domain says of it: the app has no copy of the rules. */
export const fulfillmentSummaryDto = z
  .object({
    id: z.uuid(),
    fulfillmentNumber: z.string(),
    status: z.enum(fulfillmentStatuses),
    salesOrder: z.object({ id: z.uuid(), soNumber: z.string() }),
    /** The lot of the order it ships (ADR-195); null for an order locked before the lots. */
    lotNo: z.number().int().nullable(),
    customer: z.object({ id: z.uuid(), name: z.string() }),
    brand: z.string().nullable(),
    warehouse: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
    /** The incoterm of the order: `EXW`, the customer collects; any other, Tomorrow ships (FR-M13-01). */
    incoterm: z.string().nullable(),
    syncStatus: z.enum(syncStatuses),
    shippingInstructionSentAt: z.iso.datetime().nullable(),
    acknowledgedAt: z.iso.datetime().nullable(),
    cancelRequestedAt: z.iso.datetime().nullable(),
    shippedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    totalUnits: z.number().int(),
    /** Units the warehouse shipped so far (I-11). */
    shippedUnits: z.number().int(),
    lineCount: z.number().int(),
    /** Whether a shipment says its tracking, from the warehouse or booked by hand. */
    tracked: z.boolean(),
    /**
     * FR-M13-02: Tomorrow ships it and nobody booked it yet (the filter `toBook`): no shipment says its tracking, and
     * no booking asked to the TLG shipping platform stands (ADR-231).
     */
    toBook: z.boolean(),
    /** FR-M13-06: released, and not taken by the warehouse in time (the filter `ackOverdue`). */
    ackOverdue: z.boolean(),
    /** Whether a shipment booked by hand can be recorded on it (FR-M13-02): not EXW, not cancelled. */
    trackable: z.boolean(),
    /**
     * Whether a label and a pickup can be asked to the TLG shipping platform for it (B-52, ADR-231): DDP, with the
     * warehouse and not shipped yet, no cancellation on its way.
     */
    bookable: z.boolean(),
    version: z.number().int(),
  })
  .meta({ id: "FulfillmentSummary" });
export type FulfillmentSummaryDto = z.output<typeof fulfillmentSummaryDto>;

/** A shipment with its lines, in the order of the lines of the sales order. */
export const fulfillmentDetailDto = fulfillmentSummaryDto
  .extend({
    lines: z.array(
      z.object({
        id: z.uuid(),
        soLineNo: z.number().int(),
        sku: z.object({ id: z.uuid(), skuCode: z.string().nullable(), ean: z.string().nullable() }),
        qty: z.number().int(),
        qtyShipped: z.number().int(),
      }),
    ),
  })
  .meta({ id: "FulfillmentDetail" });
export type FulfillmentDetailDto = z.output<typeof fulfillmentDetailDto>;
